import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import React, { useState } from "react";
import useOnboardingStore from "../store/onboardingStore";
import useGlobalStore from "@/store/globalStore";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { timezone } from "@/helper/options";
import SearchableDropdown from "@/components/ui/SearchDropDown";

interface Props {
  onNext: () => void;
}

const StepOneMediaAgencyOnboarding: React.FC<Props> = ({ onNext }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { sendUserData } = useOnboardingStore(
    useShallow((state) => ({
      sendUserData: state.sendUserData,
    }))
  );
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    title: "",
    password: "",
    password_confirmation: "",
    language: "",
    timezone: "",
  });

  const setError = useGlobalStore((state) => state.setError);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      first_name,
      last_name,
      title,
      password,
      password_confirmation,
      language,
      timezone,
    } = formData;

    if (!first_name.trim()) return setError("First name is required.");
    if (!last_name.trim()) return setError("Last name is required.");
    if (!title.trim()) return setError("Title is required.");
    if (!password.trim()) return setError("Password is required.");
    if (!password_confirmation.trim())
      return setError("Confirm password is required.");
    if (!language.trim()) return setError("Language is required.");
    if (!timezone.trim()) return setError("Timezone is required.");

    if (password !== password_confirmation) {
      return setError("Passwords do not match.");
    }

    try {
      setIsLoading(true);

      await sendUserData({
        first_name,
        last_name,
        title,
        password,
        password_confirmation,
        language,
        timezone,
        token: searchParams.get("token"),
      });

      onNext();
    } catch (err: any) {
      setError(err?.message || "Failed to submit user data.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#F8F8F8] flex flex-col items-center justify-center px-4 py-14">
      <div className="max-w-md w-full text-center mb-6">
        <h1 className="text-5xl font-bold text-[#EA33C0] mb-4">MediaBridge</h1>
        <h2 className="text-xl font-semibold mb-2">Welcome</h2>
        <p className="text-sm text-gray-600">
          You've been appointed Media Admin for your team. Congrats! <br />
          Please fill in the required fields so we can finish setting up your
          account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-xl space-y-4">
        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">First Name</div>
          <TextField
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={onChange}
            placeholder="Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Last Name</div>
          <TextField
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={onChange}
            placeholder="Last Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Title</div>
          <TextField
            type="text"
            name="title"
            value={formData.title}
            onChange={onChange}
            placeholder="Title"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Language</div>
          <SearchableDropdown
            selected={formData.language}
            setSelected={(e) => {
              setFormData((prev) => ({
                ...prev,
                language: e.toString(),
              }));
            }}
            options={[{ title: "English", value: "en" }]}
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Timezone</div>
          <SearchableDropdown
            selected={formData.timezone}
            setSelected={(e) => {
              setFormData((prev) => ({
                ...prev,
                timezone: e.toString(),
              }));
            }}
            options={timezone}
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Password</div>
          <TextField
            type="password"
            name="password"
            value={formData.password}
            onChange={onChange}
            placeholder="Password"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-5 w-56 text-right font-semibold">Confirm Password</div>
          <TextField
            type="password"
            name="password_confirmation"
            value={formData.password_confirmation}
            onChange={onChange}
            placeholder="Confirm Password"
            className="w-full"
          />
        </div>

        <div className="pt-4 text-center flex justify-center">
          <Button type="primary" onClick={handleSubmit} isLoading={isLoading}>
            Save Info
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StepOneMediaAgencyOnboarding;
